import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {assert} from 'Asserts';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as biojs from 'BioJS';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import {Sequence} from 'components/Sequence';
import Tags from 'components/Tags';
import {GBPreferences} from 'constants/ExportConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {REGISTRY_TYPE} from 'constants/SourceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector from 'file-browser/FolderSelector';
import {InformationPanelState} from 'FileStates';
import * as FileUtil from 'folder-items/FileUtil';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {SEQUENCE_LOCKING_HELP_TEXT} from 'help/HelpConstants';
import {showBasicHelpModal} from 'help/Modals';
import staticURL from 'lib/staticURL';
import Annotation from 'molbio/annotations/backbone/Annotation';
import {openSequenceVersion} from 'molbio/SequenceActions';
import * as sequtil from 'molbio/SeqUtil';
import CircularMapTabState from 'molbio/tabstates/CircularMapTabState';
import MiniMapTabState from 'molbio/tabstates/MiniMapTabState';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import {TabState} from 'Navigation';
import notifications from 'notifications';
import * as DeleteUtil from 'permissions/DeleteUtil';
import * as popup from 'Popup';
import preferences from 'Preferences';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as RegistryUtil from 'registry/RegistryUtil';
import {ConversionWarningModal} from 'toolbar/ConversionWarningModal';
import InformationPanelNameInput from 'toolbar/shared/InformationPanelNameInput';
import TagsSubpanel from 'toolbar/shared/TagsSubpanel';
import runModal from 'ui-library/runModal';
import {toFilename} from 'util/FileUtil';
import fuzzydate from 'util/fuzzydate';
import {typeToName} from 'util/NameUtil';
import * as net from 'util/Net';
import * as svgtools from 'util/SVGTools';
import {BasicEvent, InputChangeEvent} from 'util/Types';

import logUndefinedState from './shared/logUndefinedState';
import {renderPanelTabs} from './shared/PanelUtil';
import {SEQUENCE_INFORMATION_PANEL_INFO, SEQUENCE_INFORMATION_PANEL_TAGS} from './ToolbarConstants';

class TagEditorSubpanel extends Backbone.View<Sequence> {
  activeTag!: null;
  panelState!: InformationPanelState;

  override initialize(): void {
    this.activeTag = null;
  }

  update(panelState: InformationPanelState, sequence: Sequence): void {
    if (this.model != null) {
      this.stopListening(this.model);
    }

    this.panelState = panelState;
    this.model = sequence;
    this.listenTo(this.model, 'change', this.render.bind(this));
    this.render();
  }

  override render(): this {
    const props = {
      id: this.model.id,
      allowEdits: this.model.allowEdits(),
      tags: this.model.tags(),
      isRegistered: this.model.isRegistered(),
    };
    renderToDOM(<TagsSubpanel {...props} />, this.el, () => {
      this.panelState.trigger('resize');
    });
    return this;
  }
}

export const SequenceLockingHelp = (): JSX.Element => (
  <div>
    <p>{SEQUENCE_LOCKING_HELP_TEXT}</p>
  </div>
);

// Information panel view for Sequences
class SequenceInformationPanelView extends Backbone.View<Backbone.Model> {
  override el!: string;
  tagsView!: TagEditorSubpanel;
  trackedFields!: Array<string>;
  serializeFuncs!: {[attribute: string]: () => boolean | string | number | undefined};
  _sequence!: BioJSSequence;
  tags!: Tags;
  panelState!: InformationPanelState;
  validateFuncs!: {[attribute: string]: () => boolean};
  svgCSS!: JQueryXHR | null;
  svgFont!: HTMLElement | null;
  sequence!: Sequence;
  exportDownloadURL!: string;

  static initClass(): void {
    this.prototype.el = '#information-panel';

    this.prototype.events = {
      // @ts-expect-error
      'click .information-title': 'showInformation',
      'click .tags-title': 'showTags',
      'change .info-locked input': 'updateState',
      'change .info-circular': 'updateCircular',
      'keyup .js-start-index': 'updateState',
      'click .update-information': 'saveInformation',
      'click .info-parent a': 'openParentVersion',
      'change .info-export-format': 'updateDownloadState',
      'click .info-export-link': 'performDownload',
      'mousedown .info-export-link': 'updateExportLink',
      'dragstart .info-export-link': 'updateExportDragURL',
      'dragend .info-export-link': 'resetExportDragText',
      'click .delete button': 'deleteSequence',
      'click .locked-help a': 'showSequenceLockingHelp',
      'click .js-show-containing-folder': 'showContainingFolder',
    };
  }

  override initialize(): void {
    this.tagsView = new TagEditorSubpanel({el: this.$el.find('.tags-subpanel .panel-container')});
    this.trackedFields = ['name', 'folder_api_id', 'tagString', 'circular', 'startIndex', 'locked'];
    this.serializeFuncs = {
      name: () => this._sequence.get('name'),
      folder_api_id: () => this._sequence.folder()!.api_identifier,
      circular: () => this._sequence.get('circular'),
      startIndex: () => this._sequence.get('startIndex'),
      tagString: () =>
        _.sortBy(
          this.tags.models.map((tag) => tag.display()),
          _.identity.bind(this)
        ).join(','),
      locked: () => this._sequence.get('locked'),
    };
    this.validateFuncs = {
      name: () => this.panelState.get('name') != null && this.panelState.get('name').length > 0,
      folder_api_id() {
        return true;
      },
      circular: () => this.panelState.get('circular') != null,
      startIndex: () => {
        const startIndex = this.panelState.get('startIndex');
        return !Number.isNaN(startIndex) && parseInt(startIndex, 10) >= 1;
      },
      tagString: () => this.tags != null,
      locked: () => this._sequence.get('locked') != null,
    };

    this.svgCSS = null;
    this.svgFont = null;

    const exportAnnotationColorsPref = preferences.get(GBPreferences.EXPORT_ANNOTATION_COLORS);
    const restrictFeatTypePref = preferences.get(GBPreferences.CONVERT_NON_STANDARD_ANNOTATIONS);
    this.$el
      .find('.export .gb-options input[name="gb-export-colors"]')
      .prop('checked', exportAnnotationColorsPref);
    this.$el
      .find('.export .gb-options input[name="gb-restrict-feature-type"]')
      .prop('checked', restrictFeatTypePref);
  }

  deleteSequence(): void {
    new popup.Confirm({
      title: 'Delete Sequence',
      msg: 'Are you sure you want to delete the sequence? It cannot be recovered.',
    })
      .choice('Cancel', 'btn-af-default')
      .choice('Delete', 'btn-af-danger', () => {
        this._sequence
          .destroy()
          .done(() => {
            FileBrowserActions.removeFromWorkspace(this._sequence.get('id'));
            FolderDAOCache.invalidateFolderContents(this._sequence.folder()!.api_identifier);
          })
          .fail(notifications.notifyError);
      })
      .show();
  }

  isRegistered(): boolean {
    return (
      this._sequence.folder()?.source != null && RegistryUtil.isRegistry(this._sequence.folder()!.source)
    );
  }

  getPartialFeatureDefinition(permissions?: Permissions): {
    item: {id: string; permissions?: Permissions};
    itemState: {isRegistered: boolean};
  } {
    return {
      item: {id: this._sequence.get('id'), permissions},
      itemState: {isRegistered: this.isRegistered()},
    };
  }

  showContainingFolder(): void {
    const folder = this._sequence.folder();
    assert(folder?.api_identifier != null, 'Expected there to be a folder');
    listFolderSection(folder, FileBrowserConstants.INVENTORY_SECTION);
  }

  showSequenceLockingHelp(e: React.MouseEvent<HTMLElement>): void {
    showBasicHelpModal(e, 'Sequence Locking', <SequenceLockingHelp />);
  }

  showSubPanel(): void {
    const panel = this.panelState.get('activeSubPanel');
    renderPanelTabs(
      this.panelState,
      [
        [
          SEQUENCE_INFORMATION_PANEL_INFO,
          `${SEQUENCE_INFORMATION_PANEL_INFO}-tab`,
          `${SEQUENCE_INFORMATION_PANEL_INFO}-panel`,
          'Sequence Info',
        ],
        [
          SEQUENCE_INFORMATION_PANEL_TAGS,
          `${SEQUENCE_INFORMATION_PANEL_TAGS}-tab`,
          `${SEQUENCE_INFORMATION_PANEL_TAGS}-panel`,
          'Tags',
        ],
      ],
      this.$('.panel-title')[0]
    );
    this.$el.find('h4[class*="-title"]').parent().removeClass('active');
    this.$el.find(`.${panel}-title`).parent().addClass('active');
    this.$el.find('.panel-container').parent().hide();
    this.$el.find(`.${panel}-subpanel`).show();
    this.$el.find('.panel-content').scrollTop(0);
    this.panelState.trigger('resize');
  }

  showInformation(): void {
    this.panelState.set('activeSubPanel', SEQUENCE_INFORMATION_PANEL_INFO);
  }

  showTags(): void {
    this.panelState.set('activeSubPanel', SEQUENCE_INFORMATION_PANEL_TAGS);
  }

  openParentVersion(e: BasicEvent): void {
    e.preventDefault();
    // @ts-expect-error: Needs investigation
    const versionId = this._sequence.get('parent_version_id');
    analytics.track('editor', 'open_sequence', {
      source: 'panel-parent',
      // @ts-expect-error: Needs investigation
      sequence_api_id: this._sequence.get('parent_id'),
    });
    // @ts-expect-error: Needs investigation
    openSequenceVersion(this._sequence.get('parent_id'), versionId);
  }

  // TODO(T8547): use SVGUtil
  cacheSVGResources(): void {
    if (this.svgCSS == null) {
      $.get(staticURL('/static/build/svg.css'))
        .done((xhr) => {
          this.svgCSS = xhr;
        })
        .fail(() => {
          this.svgCSS = null;
        });
    }

    if (this.svgFont == null) {
      $.ajax({url: staticURL('/static/fonts/inconsolata-400.svg'), dataType: 'xml'})
        .done((xhr) => {
          const fontNode = $(xhr).find('font');
          const fontFace = fontNode.find('font-face');
          fontFace.attr('font-family', 'Inconsolata').attr('font-weight', 400);
          fontNode.find('glyph:not([d])').remove();
          this.svgFont = fontNode.get(0);
        })
        .fail(() => {
          this.svgFont = null;
        });
    }
  }

  updateDownloadState(): void {
    const fmt = this.$('.info-export-format').val();
    const $a = this.$('.info-export-link');
    // @ts-expect-error: Need to figure out jQuery types here
    $a.attr('disabled', fmt === '' || (fmt === 'svg' && this.sequence.isOligo()));
    if (fmt === 'svg') {
      this.cacheSVGResources();
    }
    this.$('.gb-options').toggleClass('hidden', fmt !== 'gb');
  }

  updateExportLink(): void {
    const fmt = this.$el.find('.info-export-format').val();
    const a = this.$el.find('.info-export-link').get(0);

    if (fmt === '' || (fmt === 'svg' && this.sequence.isOligo())) {
      // @ts-expect-error: Need to figure out jQuery types here
      a.setAttribute('disabled', true);
    }

    let mimeType = 'text/plain';
    const fileName = toFilename(this._sequence.get('name'), fmt);
    switch (fmt) {
      case 'gb': {
        const exportColors = this.$el
          .find('.export .gb-options input[name="gb-export-colors"]')
          .is(':checked');
        preferences.set(GBPreferences.EXPORT_ANNOTATION_COLORS, exportColors);
        const restrictFeatTypePref = this.$el
          .find('.export .gb-options input[name="gb-restrict-feature-type"]')
          .is(':checked');
        preferences.set(GBPreferences.CONVERT_NON_STANDARD_ANNOTATIONS, restrictFeatTypePref);
        // @ts-expect-error: Need to figure out jQuery types here
        a.href = FileUtil.getExportURL(this._sequence.get('editURL'), 'gb', {
          dl: true,
          nocol: !exportColors,
          restrictFeatType: restrictFeatTypePref,
        });
        // @ts-expect-error: Need to figure out jQuery types here
        a.download = null;
        break;
      }
      case 'fasta':
        // @ts-expect-error: Need to figure out jQuery types here
        a.href = FileUtil.getExportURL(this._sequence.get('editURL'), 'fasta', {dl: true});
        // @ts-expect-error: Need to figure out jQuery types here
        a.download = null;
        break;
      case 'rdf':
        // @ts-expect-error: Need to figure out jQuery types here
        a.href = FileUtil.getExportURL(this._sequence.get('editURL'), 'rdf', {dl: true});
        // @ts-expect-error: Need to figure out jQuery types here
        a.download = null;
        break;
      case 'svg':
        this.cacheSVGResources();
        if (this.svgCSS == null) {
          notifications.error('SVG CSS Missing (Benchling Internal Error)');
          // @ts-expect-error: Need to figure out jQuery types here
          a.setAttribute('disabled', true);
        } else if (this.svgFont == null) {
          notifications.error('SVG Font Missing (Benchling Internal Error)');
          // @ts-expect-error: Need to figure out jQuery types here
          a.setAttribute('disabled', true);
        } else {
          let map = $('#circular-map svg, #mini-map svg').get(0);
          if (map == null) {
            // Find the tab.
            const tab = this.panelState
              .get('buffers')
              .getTab((t: TabState) => t instanceof MiniMapTabState || t instanceof CircularMapTabState);
            if (tab != null) {
              tab.set({active: true});
            }
            map = $('#circular-map, #mini-map').get(0);
          }
          if (map) {
            a.removeAttribute('disabled');
            // @ts-expect-error: Need to figure out jQuery types here
            const data = svgtools.serialize(map, this.svgCSS, this.svgFont);
            mimeType = 'image/svg+xml';
            const blob = new Blob([data], {type: mimeType});
            // @ts-expect-error: Need to figure out jQuery types here
            a.download = fileName;

            if (/Safari/i.test(navigator.userAgent)) {
              // @ts-expect-error: Need to figure out jQuery types here
              a.href = `data:image/svg+xml,${encodeURIComponent(data)}`;
            } else {
              // @ts-expect-error: Need to figure out jQuery types here
              a.href = window.webkitURL?.createObjectURL(blob) ?? window.URL.createObjectURL(blob);
            }
          } else {
            notifications.error('Unable to locate map for export (Benchling Internal Error)');
            // @ts-expect-error: Need to figure out jQuery types here
            a.setAttribute('disabled', true);
          }
        }
        break;
      default:
        // @ts-expect-error: Need to figure out jQuery types here
        a.setAttribute('disabled', true);
    }
    // @ts-expect-error: Need to figure out jQuery types here
    this.exportDownloadURL = [mimeType, fileName, a.href].join(':');
  }

  performDownload(e: BasicEvent): void {
    if (this.$el.find('.info-export-format').val() === 'svg') {
      return;
    }
    e.preventDefault();
    net.submitCSRFForm('POST', this.$el.find('.info-export-link').attr('href'), {target: '_blank'});
  }

  updateExportDragURL(e: unknown): void {
    this.updateExportLink();
    this.$el.find('.info-export-link').removeClass('button--secondary').addClass('button--primary');
    this.$el.find('.info-export-text').text('Drag to Desktop');
    // @ts-expect-error: Appears to be a custom type, needs investigation
    e.originalEvent.dataTransfer.setData('DownloadURL', this.exportDownloadURL);
  }

  resetExportDragText(e: BasicEvent): void {
    this.$el.find('.info-export-link').removeClass('button--primary').addClass('button--secondary');
    this.$el.find('.info-export-text').text('Download');
  }

  _asyncRename(oldName: string, newName: string): Promise<void> {
    return FileBrowserActions.renameFileWithDataMods(this._sequence.get('id'), this.isRegistered(), {
      oldName,
      newName,
      notifyOnSuccess: false,
    });
  }

  _asyncSaveSequence(): JQueryPromise<unknown> {
    const sequenceUpdates: {
      circular: boolean;
      startIndex: number;
      locked: boolean;
      folder_api_id?: string;
    } = {
      circular: this.panelState.get('circular'),
      startIndex: parseInt(this.panelState.get('startIndex'), 10),
      locked: this.panelState.get('locked'),
    };
    const currentFolderId = this.panelState.get('folder_api_id');
    if (
      currentFolderId != null &&
      !RegistryUtil.isRegistryFolder(this._sequence.folder()!) &&
      (!this._sequence.folder() || currentFolderId !== this._sequence.folder()!.api_identifier)
    ) {
      sequenceUpdates.folder_api_id = this.panelState.get('folder_api_id');
    }

    return this._sequence
      .save(sequenceUpdates, {patch: true, respondDiff: true, fileUpdate: true, wait: true})
      .fail(notifications.notifyError);
  }

  _onSaveSuccess(circularToLinear: boolean): void {
    for (const field of this.trackedFields) {
      this.panelState.set(field, this.serializeFuncs[field]());
    }

    // @ts-expect-error: Need to figure out what's going on here: we use Backbone Tags for Sequence but plain objects for BioJSSequence
    this.sequence.get('tags').reset(this.sequence.get('_sequence').get('tags'));

    // If topology changes, for example.
    this.sequence.updateVersions();

    if (circularToLinear) {
      const start = this.sequence.selection().start();
      const end = this.sequence.selection().end();
      if (sequtil.isWrap(start, end)) {
        this.sequence.selection().reset(0);
      }
    }

    this.updateDownloadState();
    notifications.success('Information updated successfully.');
  }

  saveInformation(): void {
    let pair;
    if (!this.panelState.get('circular') && !this._sequence.finalized()) {
      notifications.warn('Please finalize this assembly before changing the topology.');
      return;
    }

    const doSave = async (): Promise<void> => {
      const circularToLinear = !this.panelState.get('circular') && this._sequence.get('circular');
      const updateButton = this.$el.find('.update-information');

      analytics.track('editor', 'saved_sequence_information');
      if (this._sequence.folder()!.source!.sourceType !== REGISTRY_TYPE) {
        FolderDefaults.set(FolderDefaultsKey.INVENTORY, this.panelState.get('folder_api_id'));
      }

      const promises = [];

      if (
        this.sequence.allowResidueEdits({ignoreLocked: true}) &&
        isFeatureEnabled({feature: StandardFeature.EDIT, ...this.getPartialFeatureDefinition()})
      ) {
        promises.push(this._asyncSaveSequence());
      }

      const oldName = this._sequence.get('name');
      const newName = this.panelState.get('name');
      if (oldName !== newName) {
        promises.push(this._asyncRename(oldName, newName));
      }

      if (promises.length === 0) {
        return;
      }

      try {
        // @ts-expect-error: Need to figure out jQuery types here
        updateButton.attr('disabled', true);
        this.$el.find('.update-information-text').text('Saving');
        await Promise.all(promises);
        this._onSaveSuccess(circularToLinear);
      } finally {
        this.renderUpdateButton();
      }
    };

    const annotations = this._sequence
      .annotations()
      .chain()
      .filter((a: Annotation) => sequtil.isWrap(a.start(), a.end()))
      .sortBy((a: Annotation) => a.end())
      .sortBy((a: Annotation) => a.start())
      .value();
    const primers = this._sequence.sequencePrimers();
    const pairs = this._sequence.primerPairs().filter((p) => {
      const [forward, reverse] = p.getPrimers(primers);
      return forward.end() > reverse.end();
    });
    const crossOriginParts = this._sequence
      .parts()
      .filter((part) => sequtil.isWrap(part.start(), part.end()))
      .sort((part) => part.end())
      .sort((part) => part.start());

    if (
      !this.panelState.get('circular') &&
      this._sequence.get('circular') &&
      (annotations.length > 0 || pairs.length > 0 || crossOriginParts.length > 0)
    ) {
      let name;
      const annotationData = [];
      for (const annotation of annotations) {
        name = annotation.name();
        if (name.length > 10) {
          name = `${name.slice(0, 10)}...`;
        }
        const start = annotation.start();
        const end = annotation.end();
        const length =
          end === start
            ? this.sequence.length()
            : (end - start + this._sequence.length()) % this._sequence.length();
        annotationData.push({
          name,
          start,
          end,
          length,
        });
      }
      const pairData = [];
      for (pair of pairs) {
        const [forward, reverse] = pair.getPrimers(primers);
        pairData.push({
          forwardName: forward.name(),
          forwardLocation: `+/${forward.bindPosition(this._sequence)}`,
          reverseName: reverse.name(),
          reverseLocation: `\u2013/${reverse.bindPosition(this._sequence)}`,
        });
      }
      const partsData = crossOriginParts.map((part) => ({
        name: part.name(),
        start: part.start(),
        end: part.end(),
        length: sequtil.length(part.start(), part.end(), this._sequence.length()),
      }));
      const translationsData = this._sequence.getCrossOriginTranslations().map(({name, regions}) => ({
        name,
        start: regions[0].start(),
        end: regions[regions.length - 1].end(),
        length: null,
      }));
      runModal(ConversionWarningModal, {
        annotations: annotationData,
        pairs: pairData,
        parts: partsData,
        translations: translationsData,
      }).then((result) => {
        if (result) {
          doSave.call(this);
        }
      });
    } else {
      doSave.call(this);
    }
  }

  updateCircular(e: JQueryEventObject): void {
    if (this.panelState == null) {
      logUndefinedState('SequenceInformationPanelView', 'panelState', this, e);
      return;
    }
    analytics.track('editor', 'changed_topology', {type: $(e.currentTarget).val()});
    const circular = $(e.currentTarget).val() === '1';
    this.$('.js-start-index')
      .val(circular ? 1 : this._sequence.get('startIndex'))
      // @ts-expect-error: Need to figure out jQuery types here
      .attr('disabled', circular);
    this.updateState();
    this.renderStartIndexHelp();
  }

  updateState(e?: JQueryEventObject): void {
    if (this.panelState == null) {
      logUndefinedState('SequenceInformationPanelView', 'panelState', this, e);
      return;
    }
    this.panelState.set({
      circular: this.$('.info-circular').val() === '1',
      startIndex: this.$('.js-start-index').val(),
      locked: this.$('.info-locked :checked').val() === '1',
    });
  }

  updateFolder(): void {
    analytics.track('editor', 'changed_library', {
      folder_api_id: this._sequence.folder()!.api_identifier,
    });
    this.updateState();
  }

  renderUpdateButton(): void {
    const changes = this.trackedFields.map(
      (field) => this.panelState.get(field) !== this.serializeFuncs[field]()
    );
    const fieldsValid = this.trackedFields.map((field) => this.validateFuncs[field]());
    this.$('.update-information-text').text('Update Information');
    // @ts-expect-error: Need to figure out jQuery types here
    this.$('.update-information').attr('disabled', !_.all(fieldsValid) || !_.any(changes));
  }

  _disableFolderSelector(): boolean {
    return !this._sequence.get('writable');
  }

  renderFolderSelector(): void {
    // @ts-expect-error: Needs investigation
    if (this._sequence.get('shared') || RegistryUtil.isRegistryFolder(this._sequence.get('folder')!)) {
      this.$el.find('.folder-selector-container').closest('.row').addClass('hidden');
    } else {
      this.$el.find('.folder-selector-container').closest('.row').removeClass('hidden');
      const selectedId = this.panelState.get('folder_api_id');
      renderToDOM(
        <FolderSelector
          value={selectedId}
          onChange={(e) => {
            this.panelState.set({folder_api_id: e.target.value});
            this.updateFolder();
          }}
          readOnly={this._disableFolderSelector()}
          showInventory={true}
          currentTagSchema={this.sequence.tagSchema()}
          unsetOnFetchFail={true}
        />,
        this.$el.find('.folder-selector-container')[0]
      );
    }
  }

  renderStartIndexHelp(): void {
    let title;
    this.$('.js-start-index-help').tooltip('destroy');
    if (this.panelState.get('circular')) {
      title = 'The index of the first base cannot be changed for circular sequences.';
    } else {
      title = 'The index of the first base in the sequence. Must be greater than 0.';
    }
    this.$('.js-start-index-help').tooltip({title, container: 'body'});
  }

  renderUndeletableHelp(show: boolean): void {
    this.$('.js-delete-help').tooltip('destroy');

    if (show) {
      const tooltipTitle = DeleteUtil.getUndeletableTooltipTitle({
        isArchived: this.sequence.isArchived(),
      });
      // @ts-expect-error Title does not exist in TooltipOptions
      this.$('.js-delete-help').tooltip({title: tooltipTitle, container: 'body'});
    }
  }

  renderDeleteButton(): void {
    if (BenchlingConfig.ENABLE_DELETE) {
      const isUndeletable = !this._sequence.get('permissions').deletable;
      this.renderUndeletableHelp(isUndeletable);
      this.$el.find('.delete button').prop('disabled', isUndeletable);
      this.$el.find('.info-delete-text').text(`Delete ${typeToName(this.sequence.subtype())}`);
    } else {
      this.$el.find('.delete').hide();
    }
  }

  _handleChangeName(e: InputChangeEvent): void {
    this.panelState.set({name: e.target.value});
  }

  renderName(): void {
    const infoNameEl = this.$el.find('.info-name')[0];
    renderToDOM(
      <InformationPanelNameInput
        archiveRecord={this._sequence.get('archiveRecord')}
        id={this._sequence.get('id')}
        isWritable={
          this._sequence.get('writable') &&
          isFeatureEnabled({feature: StandardFeature.EDIT, ...this.getPartialFeatureDefinition()})
        }
        name={this.panelState.get('name')}
        onChange={this._handleChangeName.bind(this)}
      />,
      infoNameEl,
      () => {
        this.panelState.trigger('resize');
      }
    );
  }

  renderArchiveButton(): void {
    if (!BenchlingConfig.ENABLE_ARCHIVING_V2) {
      return;
    }
    const archiveEl = this.$el.find('.archive')[0];
    const permissions = this._sequence.get('permissions');
    const isUnarchivable =
      permissions &&
      isFeatureEnabled({
        feature: StandardFeature.UNARCHIVE,
        ...this.getPartialFeatureDefinition(permissions),
      });
    const isArchivable =
      permissions &&
      isFeatureEnabled({feature: StandardFeature.ARCHIVE, ...this.getPartialFeatureDefinition(permissions)});
    renderToDOM(
      <div className="row">
        <div className="col col-md-12">
          <ArchiveOrUnarchiveButton
            archiveRecord={this._sequence.get('archiveRecord')}
            fullWidth={true}
            isArchivable={isArchivable}
            isUnarchivable={isUnarchivable}
            item={{
              id: this._sequence.get('id'),
              name: this._sequence.get('name'),
              archiveRecord: this._sequence.get('archiveRecord'),
              folder: this._sequence.folder(),
              // @ts-expect-error: Needs investigation
              projectFolder: this._sequence.get('projectFolder'),
            }}
            onArchive={ArchiveActions.bulkArchiveFilesWithDataMods}
            onUnarchive={ArchiveActions.bulkUnarchiveFilesWithDataMods}
            showText={true}
          />
        </div>
      </div>,
      archiveEl,
      () => {
        this.panelState.trigger('resize');
      }
    );
  }

  override render(): this {
    this.showSubPanel();

    const createdDate = fuzzydate(this._sequence.get('created_at'));
    this.$el.find('.info-creator').text(this._sequence.get('creator'));
    this.$el.find('.info-created').text(createdDate);
    this.$el.find('.info-length').text(biojs.util.formatBaseCount(this._sequence.length()));

    // Re-render option for exporting.
    const svgExportOptions = this.$el.find('.info-export-format option[value=svg]');
    if (this._sequence.get('circular')) {
      svgExportOptions.text('Vector Plasmid Map (.svg)');
    } else {
      svgExportOptions.text('Vector Linear Map (.svg)');
    }
    this.updateDownloadState();

    // @ts-expect-error: Needs investigation
    if (!this._sequence.get('shared') && this._sequence.get('parent_id') != null) {
      this.$el
        .find('.info-parent a')
        // @ts-expect-error: Needs investigation
        .attr('href', this._sequence.get('parent_url'))
        // @ts-expect-error: Needs investigation
        .text(`${this._sequence.get('parent')} ("${this._sequence.get('parent_version_name')}")`)
        .closest('.row')
        .removeClass('hidden');
    } else {
      this.$el.find('.info-parent').closest('.row').addClass('hidden');
    }

    this.renderName();
    this.renderFolderSelector();
    this.renderArchiveButton();

    const circular = this._sequence.get('circular');
    this.$el.find('.info-circular').val(Number(circular));
    this.$el.find('.js-start-index').val(this._sequence.get('startIndex'));
    this.renderStartIndexHelp();
    const lockedVal = Number(this._sequence.get('locked'));
    this.$el.find(`.info-locked input[value=${lockedVal}]`).prop('checked', true);

    this.renderDeleteButton();

    const isEditFeatureEnabled = isFeatureEnabled({
      feature: StandardFeature.EDIT,
      ...this.getPartialFeatureDefinition(),
    });

    if (this._sequence.get('writable') && isEditFeatureEnabled) {
      this.$el.find('input[type="text"], textarea').prop('readonly', false);
      this.$el.find('input[type="radio"]').prop('disabled', false);
      this.$el.find('select').prop('disabled', false);
      this.$el.find('.update-information').show();
      this.renderUpdateButton();
    } else {
      this.$el.find('input[type="text"], textarea').prop('readonly', true);
      this.$el.find('input[type="radio"]').prop('disabled', true);
      this.$el.find('select').prop('disabled', true);
      this.$el.find('.export select').prop('disabled', false);
      this.$el.find('.update-information').hide();
    }
    if (
      this.sequence.allowResidueEdits() &&
      isEditFeatureEnabled &&
      !this.sequence.isOligo() &&
      !this.sequence.isRNASequence()
    ) {
      this.$el.find('select.info-circular').prop('disabled', false);
      this.$el.find('.js-start-index').prop('readonly', circular);
    } else {
      this.$el.find('select.info-circular').prop('disabled', true);
      this.$el.find('.js-start-index').prop('readonly', true);
    }
    if (!(this.sequence.allowResidueEdits({ignoreLocked: true}) && isEditFeatureEnabled)) {
      this.$el.find('.info-locked input').prop('disabled', true);
    }

    const $showContainingFolder = this.$('.js-show-containing-folder');
    const inFolder =
      this._sequence.has('folder') && !RegistryUtil.isRegistryFolder(this._sequence.get('folder')!);
    $showContainingFolder.toggleClass('hidden', !inFolder);
    if (inFolder) {
      $showContainingFolder.tooltip({container: 'body'});
    } else {
      $showContainingFolder.tooltip('destroy');
    }
    return this;
  }

  update(panelState: InformationPanelState, sequence: Sequence): void {
    this.panelState = panelState;
    this.sequence = sequence;
    if (this._sequence != null) {
      this.stopListening(this._sequence);
    }
    this._sequence = sequence.get('_sequence');

    this.tags = sequence.get('tags') as unknown as Tags;
    this.tagsView.update(this.panelState, sequence);

    for (const field of this.trackedFields) {
      if (panelState.get(field) == null) {
        this.panelState.set(field, this.serializeFuncs[field]());
      }
    }

    this.listenTo(this.panelState, 'change:activeSubPanel', this.showSubPanel.bind(this));
    this.listenTo(this._sequence, 'change:writable', this.render.bind(this));
    this.listenTo(this._sequence, 'change:circular', this.render.bind(this));
    this.listenTo(this._sequence, 'change:startIndex', this.render.bind(this));
    this.listenTo(this._sequence, 'change:bases', this.render.bind(this));
    this.listenTo(this._sequence, 'change:locked', this.render.bind(this));
    this.listenTo(this.sequence, 'change:currentVersion', this.render.bind(this));
    this.listenTo(this._sequence, 'change', this.renderUpdateButton.bind(this));
    this.listenTo(this.panelState, 'change', this.renderUpdateButton.bind(this));
    this.listenTo(this.panelState, 'change:name', this.renderName.bind(this));
    this.listenTo(this.panelState, 'change:folder_api_id', this.renderFolderSelector.bind(this));
    this.listenTo(this._sequence, 'change:name', this.renderName.bind(this));
    this.listenTo(this._sequence, 'change:folder', this.renderFolderSelector.bind(this));
    this.listenTo(this.tags, 'change add remove', this.renderUpdateButton.bind(this));
    this.listenTo(this._sequence, 'change:archiveRecord', this.renderDeleteButton.bind(this));

    this.render();
  }
}

SequenceInformationPanelView.initClass();

export default SequenceInformationPanelView;
